"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { WWindowBannerContent, WWindowBannerItem } from "@/lib/api/cms-window-content";
import { useT } from "@/lib/i18n/context";
import { wWindowBannerTranslations, type WWindowBannerTranslations } from "./WWindowBanner.i18n";
import styles from "./WWindowBanner.module.css";

/**
 * WWindowBanner - renderer okna `w_window_banner` (okno banerowe).
 *
 * **Dwa tryby wyświetlania** (`config.display_mode`, połączenie z dawnym
 * `w_popup` - 2026-06-17):
 *   - `inline` - banner renderowany w treści strefy layoutu (np. `main`).
 *   - `popup` - banner jako modal nakładany na stronę po wejściu, z kontrolą
 *     częstotliwości (`always` / `once` na N dni przez localStorage).
 *
 * **Parytet z legacy:**
 *   - inline: `component/w_window_banner/VIEW/*` (obraz/tekst + autorotacja)
 *   - popup: `component/w_popup/*` (modal colorbox + cookie "pokaż raz")
 *
 * Banner = obraz (z opcjonalnym linkiem) ALBO blok treści HTML/JS. Rotacja w
 * pełni po stronie klienta (backend wysyła komplet bannerów). `text` (type 3/4)
 * to HTML z edytora - `dangerouslySetInnerHTML`, trust source: tylko z IADMIN.
 *
 * Dispatcher (`WindowRenderer`) wstrzykuje `content` z
 * `WindowContentResponse.data.response_window_data`.
 */
export function WWindowBanner({ content }: WWindowBannerProps) {
  const items = content?.items ?? [];

  if (items.length === 0 || !content) {
    /* W trybie popup pusty zestaw = nic nie renderujemy (brak modala).
       W inline pokazujemy delikatny komunikat tylko gdy content istnieje. */
    return content && content.config?.display_mode !== "popup" ? (
      <EmptyState />
    ) : null;
  }

  if (content.config?.display_mode === "popup") {
    return <BannerPopup content={content} />;
  }

  return (
    <div className={styles.root}>
      <BannerCarousel content={content} />
    </div>
  );
}

export type WWindowBannerProps = {
  content: WWindowBannerContent | null;
};

/* ─── Stan pusty (tylko inline) ───────────────────────────────────────── */

function EmptyState() {
  const t = useT(wWindowBannerTranslations);
  return (
    <div className={styles.empty} role="status">
      {t.empty}
    </div>
  );
}

/* ─── Karuzela bannerów (wspólna dla inline i popup) ──────────────────── */

function BannerCarousel({ content }: { content: WWindowBannerContent }) {
  const t = useT(wWindowBannerTranslations);
  const items = content.items;
  const rotate = !!content.config?.rotate && items.length > 1;
  const freq = content.config?.freq_rotate ?? 5;

  const [activeIndex, setActiveIndex] = useState(0);

  /* Autorotacja - co `freq` sek. kolejny banner (zapętlone). setActiveIndex w
     formie funkcyjnej, więc activeIndex nie musi być w deps (brak restartu
     interwału co tick). */
  useEffect(() => {
    if (!rotate) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, freq * 1000);
    return () => clearInterval(id);
  }, [rotate, freq, items.length]);

  if (items.length === 1) {
    return <Banner item={items[0]} altFallback={t.bannerAlt} />;
  }

  return (
    <div aria-roledescription="carousel">
      <div className={styles.stage}>
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`${styles.layer} ${index === activeIndex ? styles.layerActive : ""}`}
            aria-hidden={index === activeIndex ? undefined : true}
          >
            <Banner item={item} altFallback={t.bannerAlt} />
          </div>
        ))}
      </div>

      <div className={styles.dots} role="tablist">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={index === activeIndex ? styles.dotActive : styles.dot}
            aria-label={t.goToSlide(index + 1)}
            aria-selected={index === activeIndex}
            role="tab"
            onClick={() => setActiveIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Tryb popup (modal) ──────────────────────────────────────────────── */

function BannerPopup({ content }: { content: WWindowBannerContent }) {
  const t = useT(wWindowBannerTranslations);
  /* `open=false` na starcie pełni rolę guardu SSR: serwer i pierwszy render
     klienta nie tworzą portalu (brak hydration mismatch). Efekt poniżej decyduje
     o otwarciu dopiero po hydration na podstawie localStorage. */
  const [open, setOpen] = useState(false);

  const storageKey = `wbanner_popup_${content.window_id}_dismissed`;
  const frequency = content.config?.popup_frequency ?? "once";
  const days = content.config?.popup_days ?? 7;

  /* Decyzja o pokazaniu - po mount (localStorage to system zewnętrzny,
     niedostępny na SSR). Dla `once` sprawdzamy timestamp ostatniego zamknięcia;
     w oknie `days` nie pokazujemy ponownie. */
  useEffect(() => {
    if (frequency === "once") {
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
          const last = parseInt(raw, 10);
          if (!Number.isNaN(last) && Date.now() - last < days * 24 * 60 * 60 * 1000) {
            return; //jeszcze w oknie - nie pokazuj
          }
        }
      } catch {
        /* localStorage niedostępny (private mode) - pokaż mimo to. */
      }
    }

    /* Otwarcie popupa po odczycie systemu zewnętrznego (localStorage) - celowe,
       deferred client-only (analog do mount-guarda w AvatarUploadModal). */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(true);
  }, [frequency, days, storageKey]);

  const close = useCallback(() => {
    setOpen(false);
    if (frequency === "once") {
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch {
        /* ignore - brak localStorage oznacza pokaz przy kazdym wejsciu. */
      }
    }
  }, [frequency, storageKey]);

  /* ESC zamyka modal. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={content.title}
      onClick={close}
    >
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.closeBtn}
          aria-label={t.close}
          onClick={close}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <BannerCarousel content={content} />
      </div>
    </div>,
    document.body,
  );
}

/* ─── Pojedynczy banner (obraz lub tekst) ─────────────────────────────── */

function Banner({ item, altFallback }: { item: WWindowBannerItem; altFallback: string }) {
  /* Type 3/4 - blok HTML. */
  if (item.text) {
    return (
      <div
        className={styles.text}
        dangerouslySetInnerHTML={{ __html: item.text }}
      />
    );
  }

  /* Type 2 - obraz. Pliki na hoście backendu Imperial -> raw <img>
     (parytet z WPrezentation/WLatestNews; next/image wymagałoby remotePatterns). */
  if (!item.image_url) return null;

  const img = (
    <img
      className={styles.image}
      src={item.image_url}
      alt={item.name || altFallback}
      width={item.width ?? undefined}
      height={item.height ?? undefined}
      loading="lazy"
    />
  );

  if (item.address) {
    return (
      <a href={item.address} target={item.target ?? undefined} className={styles.link}>
        {img}
      </a>
    );
  }

  return img;
}

export type { WWindowBannerTranslations };
